import {
  COLUMN_SLUG,
  COLUMNS_SLUG,
  DIV_SLUG,
  GALLERY_SLUG,
  GRID_SLUG,
  ICON_SLUG,
  IMAGE_SLUG,
  SECTION_SLUG,
  WRAPPER_LINK_SLUG,
  WP_MENU_SLUG,
  ADVANCED_SLIDER_SLUG,
  ADVANCED_SLIDE_SLUG,
  HEADER_BUILDER_SLUG,
  TEXT_SLUG,
  HEADING_SLUG
} from "@/lib/elements/constants";
import {
  DISABLED_ELEMENT_BEHAVIOR,
  FutureLayerElementBehavior,
  registerElementBehavior
} from "./element-behavior";
import { singularityStore, uiStore, documentStore } from "@/store/store";
import { TreeNode } from "@/types/tree";
import { findNode } from "@/lib/tree/tree";
import {
  FUTURELAYER_HEADER_ELEMENT_TYPE,
  FUTURELAYER_SITELOGO_ELEMENT_TYPE
} from "@/futurelayer/headers-footers/headers-footers";
import { getClickedGalleryImageIndex } from "@/lib/singularity/gallery";

export function registerFutureLayerElementBehaviors() {
  registerElementBehaviorForImageElements();
  registerElementBehaviorForContainers();
  registerElementBehaviorForOther();
  registerElementBehaviorForMenuAndSiteLogo();
  registerElementBehaviorForTextElements();
}

function registerElementBehaviorForTextElements() {
  registerElementBehavior([TEXT_SLUG, HEADING_SLUG], {
    canActivate: true,
    initialPanel: "text_editor",
    setPanelState: (textElement: TreeNode) => {
      singularityStore.setTextEditorPanelState({
        elementId: textElement.id,
        contentEditablePropertyPath: "content.content.text"
      });
    }
  });
}

function registerElementBehaviorForImageElements() {
  registerElementBehavior(IMAGE_SLUG, {
    canActivate: true,
    initialPanel: "image_editor",
    setPanelState: (imageElement: TreeNode) => {
      singularityStore.setImageEditorPanelState({
        elementId: imageElement.id,
        controlPath: "content.image.media",
        initialStep: "main"
      });
    }
  });

  registerElementBehavior(GALLERY_SLUG, {
    canActivate: true,
    initialPanel: "image_editor",
    setPanelState: (galleryElement: TreeNode) => {
      singularityStore.setImageEditorPanelState({
        elementId: galleryElement.id,
        controlPath: "content.content.images[].image",
        selectedImageIndex: getClickedGalleryImageIndex() ?? 0,
        initialStep: "main"
      });
    }
  });
}

function registerElementBehaviorForContainers() {
  const SECTION_AND_HEADER_BEHAVIOR: FutureLayerElementBehavior = {
    canActivate: true,
    initialPanel: "none",
    setPanelState: () => {
      if (uiStore.inlineEditingContext?.elementId) {
        const inlineEditingElement = findNode(
          documentStore.document.tree,
          uiStore.inlineEditingContext.elementId
        );
        if (inlineEditingElement) {
          const elementType =
            inlineEditingElement.data.type === FUTURELAYER_HEADER_ELEMENT_TYPE
              ? "header"
              : "footer";
          singularityStore.setHeaderAndFooterEditorPanelState({
            elementId: inlineEditingElement.id,
            elementType: elementType
          });
        }
      } else {
        singularityStore.setSingularityLeftPanel("page_structure");
      }
    }
  };

  registerElementBehavior(SECTION_SLUG, SECTION_AND_HEADER_BEHAVIOR);
  registerElementBehavior(HEADER_BUILDER_SLUG, SECTION_AND_HEADER_BEHAVIOR);

  registerElementBehavior(COLUMNS_SLUG, DISABLED_ELEMENT_BEHAVIOR);
  registerElementBehavior(COLUMN_SLUG, DISABLED_ELEMENT_BEHAVIOR);
  registerElementBehavior(DIV_SLUG, DISABLED_ELEMENT_BEHAVIOR);
  registerElementBehavior(GRID_SLUG, DISABLED_ELEMENT_BEHAVIOR);
  registerElementBehavior(ADVANCED_SLIDER_SLUG, DISABLED_ELEMENT_BEHAVIOR);
  registerElementBehavior(ADVANCED_SLIDE_SLUG, DISABLED_ELEMENT_BEHAVIOR);
  registerElementBehavior(WRAPPER_LINK_SLUG, DISABLED_ELEMENT_BEHAVIOR);
}

function registerElementBehaviorForOther() {
  registerElementBehavior(ICON_SLUG, {
    canActivate: true,
    initialPanel: "icon_editor",
    setPanelState: icon => {
      singularityStore.setIconEditorPanelState({
        iconId: icon.id
      });
    }
  });
}

function registerElementBehaviorForMenuAndSiteLogo() {
  registerElementBehavior(WP_MENU_SLUG, {
    canActivate: true,
    initialPanel: "page_manager",
    setPanelState: () => {
      singularityStore.setSingularityLeftPanel("page_manager");
    }
  });

  registerElementBehavior(FUTURELAYER_SITELOGO_ELEMENT_TYPE, {
    canActivate: true,
    initialPanel: "brand",
    setPanelState: () => {
      singularityStore.setSingularityBrandPanelState({
        openedPanel: "logo"
      });
    }
  });
}
